import type { LucideIcon } from 'lucide-react';
import { EyeOff, Eye, ShoppingCart } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StartingPoint = 'not-aware' | 'aware-not-considered' | 'no-reason-not-to';

export interface Friction {
  id: string;
  title: string;
  /** What the customer might be thinking. */
  customerThought: string;
}

export interface ActionIdea {
  id: string;
  title: string;
  description: string;
  /** Where on the platform this links to (optional). */
  href?: string;
  category: 'online' | 'irl' | 'both';
}

export interface StartingPointData {
  id: StartingPoint;
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  /** Colour classes for visual distinction. */
  accent: {
    bg: string;
    border: string;
    text: string;
    iconBg: string;
  };
  /** Why this matters — brief education. */
  education: string;
  /** Common friction signals under this path. */
  frictions: Friction[];
  /** Action ideas to explore. */
  actions: ActionIdea[];
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export const STARTING_POINTS: StartingPointData[] = [
  {
    id: 'not-aware',
    title: 'Not aware of the product',
    subtitle: "They can't buy what they don't know exists",
    description:
      "People simply haven't encountered your product yet. They haven't seen it online, in store, or heard about it from someone they trust.",
    icon: EyeOff,
    accent: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-900',
      iconBg: 'bg-amber-100',
    },
    education:
      "This is a visibility problem, not a product problem. No amount of product improvement will help if people haven't seen it. The job is to get the product in front of more eyeballs — online and in real life — in contexts where people are already paying attention.",
    frictions: [
      {
        id: 'not-visible-online',
        title: 'Not visible enough online',
        customerThought: "I've never come across this product in my feed or searches.",
      },
      {
        id: 'not-visible-irl',
        title: 'Not visible in real life',
        customerThought: "I've never seen this on a shelf or at an event.",
      },
      {
        id: 'no-word-of-mouth',
        title: 'No word of mouth',
        customerThought: "Nobody I know has mentioned it.",
      },
    ],
    actions: [
      {
        id: 'ramp-social',
        title: 'Ramp up social presence',
        description:
          'Post more consistently on Instagram and TikTok. Show the product being used, not just product shots.',
        category: 'online',
      },
      {
        id: 'collab-posts',
        title: 'Collaborate with complementary brands',
        description:
          'Do recipe or lifestyle collab posts with other brands that have the audience you want. Tap into their following.',
        category: 'online',
      },
      {
        id: 'tag-venues',
        title: 'Tag in at more venues and locations',
        description:
          'Get tagged in posts from cafes, stores, and venues that stock you. Makes the product feel present.',
        category: 'online',
      },
      {
        id: 'markets-popups',
        title: 'Markets, popups, and stalls',
        description:
          'Be physically present where your target audience browses. Farmers markets, food events, community popups.',
        category: 'irl',
      },
      {
        id: 'more-stockists',
        title: 'Get into more places people shop',
        description:
          'More points of physical availability. Visible on more shelves in more locations.',
        category: 'irl',
      },
      {
        id: 'giveaways',
        title: 'Creative giveaways',
        description:
          'Run giveaways that get people to tag friends and share. "Best use of [product] in a dish" or "comment and tag someone who would love this".',
        category: 'online',
      },
    ],
  },
  {
    id: 'aware-not-considered',
    title: 'Aware but not considered buying',
    subtitle: "They've seen it but haven't thought about putting it in their basket",
    description:
      "People have encountered your product — they've seen it on a shelf or online — but it hasn't registered as something for them. They haven't seriously considered buying it.",
    icon: Eye,
    accent: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-900',
      iconBg: 'bg-blue-100',
    },
    education:
      "This is a relevance or confidence problem. They've seen it but something isn't connecting. Maybe they don't understand what it is, don't see how it fits into their life, aren't into the flavour profile, or the price point makes them hesitate without a reason to try. The job is to make the product feel relevant, desirable, or at least worth a try.",
    frictions: [
      {
        id: 'dont-understand',
        title: "Don't understand the product",
        customerThought: "I'm not sure what this actually is or what I'd do with it.",
      },
      {
        id: 'not-for-me',
        title: "Doesn't feel like it's for me",
        customerThought: "I can see it exists but it doesn't feel like my kind of thing.",
      },
      {
        id: 'flavour-profile',
        title: 'Flavour or sensory hesitation',
        customerThought: "I'm not sure I'd like the taste/smell/texture.",
      },
      {
        id: 'price-hesitation',
        title: "Price makes me pause — don't want to waste money",
        customerThought: "It's not cheap, and I don't know if I'll like it.",
      },
      {
        id: 'need-to-try',
        title: 'Need to try it before committing',
        customerThought: "I'd probably buy it if I could taste it first.",
      },
    ],
    actions: [
      {
        id: 'sampling-tastings',
        title: 'Sampling and tastings',
        description:
          'Let people try the product. Markets, in-store demos, event sampling. Removes the risk of spending money on something unknown.',
        href: '/themes/sampling-tastings',
        category: 'irl',
      },
      {
        id: 'show-use-cases',
        title: 'Show how it fits into real life',
        description:
          'Content showing the product in context — recipes, pairings, occasions. Answer "when would I use this?" before they have to ask.',
        category: 'online',
      },
      {
        id: 'social-proof',
        title: 'Build social proof',
        description:
          'Reviews, UGC, testimonials from people similar to your target. Let others do the convincing.',
        category: 'online',
      },
      {
        id: 'smaller-entry',
        title: 'Offer a smaller entry point',
        description:
          'A smaller size, a sample pack, a bundle. Reduce the financial risk of trying something new.',
        category: 'both',
      },
      {
        id: 'events-hosting',
        title: 'Host or sponsor events',
        description:
          "Put the product in hands at events where your target audience already is. Competition sponsorship, event catering, goodie bags.",
        category: 'irl',
      },
      {
        id: 'education-content',
        title: 'Create educational content about the category',
        description:
          "If people don't understand the product type, educate on the category. What is it, where does it come from, how do people use it.",
        category: 'online',
      },
    ],
  },
  {
    id: 'no-reason-not-to',
    title: 'No reason not to buy — just not buying',
    subtitle: "Could be great to try or keep in the house, but they're not",
    description:
      "There's no obvious objection. It's not too expensive, they're not against it, they might even think it's a good product. They're just... not buying. It hasn't crossed the line from 'would be nice' to 'I'm putting it in my basket'.",
    icon: ShoppingCart,
    accent: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-900',
      iconBg: 'bg-emerald-100',
    },
    education:
      "This is a habit or trigger problem. There's no active resistance — there's just no active motivation either. The product isn't top of mind at the moment of purchase, or there isn't a clear enough trigger to go buy it. The job is to create a reason to buy now, or make it easy enough that the tiny amount of effort required drops to zero.",
    frictions: [
      {
        id: 'not-top-of-mind',
        title: 'Not top of mind at point of purchase',
        customerThought: "I just don't think of it when I'm shopping.",
      },
      {
        id: 'no-trigger',
        title: "No trigger to go buy it",
        customerThought: "There's nothing making me think 'I should grab one of those'.",
      },
      {
        id: 'already-have-alternative',
        title: 'Already have a go-to alternative',
        customerThought: "I already buy [other thing] and it's fine. No reason to switch.",
      },
      {
        id: 'too-expensive-for-habit',
        title: "Too expensive to become a regular purchase — how was that decided?",
        customerThought: "It's nice but not at that price every week.",
      },
    ],
    actions: [
      {
        id: 'subscription-repeat',
        title: 'Make repeat purchase effortless',
        description:
          'Subscription, auto-reorder, or regular delivery options. Remove the decision from each purchase.',
        category: 'online',
      },
      {
        id: 'occasion-triggers',
        title: 'Create occasion triggers',
        description:
          'Tie the product to specific moments — "Friday night with [product]", "your morning [product] ritual". Give people a when.',
        category: 'both',
      },
      {
        id: 'bundle-with-routine',
        title: 'Bundle with existing routines',
        description:
          'Pair with products people already buy regularly. Cross-promotions, recipe packs, "goes great with" positioning.',
        category: 'both',
      },
      {
        id: 'limited-time',
        title: 'Create urgency or scarcity',
        description:
          'Limited runs, seasonal flavours, collab editions. Give a reason to buy now rather than "someday".',
        category: 'both',
      },
      {
        id: 'placement-visibility',
        title: 'Improve in-store placement',
        description:
          "End caps, eye-level shelf position, near complementary products. Be where they're already looking.",
        category: 'irl',
      },
      {
        id: 'comparison-content',
        title: "Give them a reason to switch from their current go-to",
        description:
          "Direct comparison content. What's different about this vs what they currently buy. Be honest about the trade-off.",
        category: 'online',
      },
    ],
  },
];

export const STARTING_POINTS_BY_ID = Object.fromEntries(
  STARTING_POINTS.map((sp) => [sp.id, sp])
) as Record<StartingPoint, StartingPointData>;
